import { createSelector } from '@ngrx/store';
import { AppState } from '../index';
import { MonitoringState } from './monitoring.reducer';
import { MetricWithData } from '../../shared/models/metric-with-data.model';
import { MetricFilteringDataFragment } from '../../shared/models/metric-data-fragment.model';

export const $monitoringState = (state: AppState): MonitoringState =>
    state.monitoring;

export const $alertsOnly = createSelector(
    $monitoringState,
    state => state.alertsOnly,
);

export const $isReport = createSelector(
    $monitoringState,
    state => state.isReport,
);

export const $metricsStatus = createSelector(
    $monitoringState,
    state => state.metricsStatus,
);

export const $sitesFocus = createSelector(
    $monitoringState,
    ({ focus }) => focus,
);

export const $siteAlerts = createSelector(
    $monitoringState,
    ({ alerts }) => alerts,
);

export const $currentAlerts = createSelector(
    $monitoringState,
    state => state.currentAlerts,
);

export const $siteReports = createSelector(
    $monitoringState,
    ({ reports }) => reports,
);

export const $smtpStatus = createSelector(
    $monitoringState,
    site => site.smtpStatus,
);

export const $deviceCareInstallation = createSelector(
    $monitoringState,
    state => state.installationParameters,
);

export const $metricsUpdateLoading = createSelector(
    $monitoringState,
    ({ metricsUpdateLoading }): boolean => metricsUpdateLoading,
);

export const $reportUpdateLoading = createSelector(
    $monitoringState,
    ({ reportUpdateLoading }): boolean => reportUpdateLoading,
);

export const $siteMetrics = createSelector(
    $monitoringState,
    state => state.metrics,
);

export const $siteReportMetrics = createSelector(
    $monitoringState,
    state => state.reportMetrics,
);

export const $siteDeviceCare = createSelector(
    $monitoringState,
    state => state.deviceCare,
);

export const $metricsWithData = createSelector(
    $monitoringState,
    (state): MetricWithData[] => state.metrics || [],
);

export const $metricNames = createSelector($monitoringState, state => {
    return state.metrics?.map(metric => ({ id: metric.id, name: metric.name }));
});

export const $metricGroups = createSelector($metricsWithData, metrics => [
    ...new Set(metrics.map(({ group }) => group)),
]);

export const $metricWithData = (metricId: string) =>
    createSelector($metricsWithData, metrics =>
        metrics.find(metric => metric.id === metricId),
    );

export const $deviceCareConfig = createSelector(
    $monitoringState,
    state => state.deviceCare,
);

export const $selectedPeriod = createSelector(
    $monitoringState,
    state => state.deviceCare?.selectedPeriod,
);

export const $reportMetricsAllLoaded = createSelector(
    $metricsWithData,
    $siteReportMetrics,
    (metrics, reportMetrics): boolean => {
        if (!reportMetrics.length) return true;
        const reportSet = new Set(reportMetrics);
        const relevant = metrics.filter(m => reportSet.has(m.id) && m.history);
        if (!relevant.length) return true;
        return relevant.every(m => m.data !== undefined);
    },
);

export const $metricFilteringData = (metricId: string) =>
    createSelector(
        $metricsWithData,
        (metrics: MetricWithData[]): MetricFilteringDataFragment[] | undefined =>
            (metrics || [])
                .map(metric => ({
                    id: metric.id,
                    filteringData: metric.filteringData,
                }))
                .filter(
                    (
                        metric,
                    ): metric is {
                        id: string;
                        filteringData: MetricFilteringDataFragment[];
                    } => metric.filteringData != undefined,
                )
                .find(metric => metric.id === metricId)?.filteringData,
    );
